import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getConsentStatus,
  submitConsent,
} from "../services/consent.service";
import { computeIsConsentValid } from "../../util/consentVersion";

const initialState = {
  status: "idle",
  hasValidConsent: false,
  currentVersion: null,
  acceptedVersion: null,
  expiresAt: null,
  isSubmitting: false,
  error: null,
  submitError: null,
};

export const fetchConsentStatus = createAsyncThunk(
  "consent/fetchStatus",
  async (_, { rejectWithValue }) => {
    try {
      const apiResponse = await getConsentStatus();
      return apiResponse?.data?.data ?? null;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

export const acceptConsent = createAsyncThunk(
  "consent/accept",
  async (_, { rejectWithValue }) => {
    try {
      const apiResponse = await submitConsent({ accepted: true });
      return apiResponse?.data?.data ?? null;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

const applyStatusPayload = (state, payload) => {
  if (!payload) return;
  state.hasValidConsent = Boolean(payload.hasValidConsent);
  state.currentVersion = payload.currentVersion ?? null;
  state.acceptedVersion = payload.acceptedVersion ?? null;
  state.expiresAt = payload.expiresAt ?? null;
};

const consentSlice = createSlice({
  name: "consent",
  initialState,
  reducers: {
    resetConsent: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(fetchConsentStatus.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchConsentStatus.fulfilled, (state, action) => {
        state.status = "ready";
        applyStatusPayload(state, action.payload);
      })
      .addCase(fetchConsentStatus.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload || "Failed to load consent status.";
      })
      .addCase(acceptConsent.pending, (state) => {
        state.isSubmitting = true;
        state.submitError = null;
      })
      .addCase(acceptConsent.fulfilled, (state, action) => {
        state.isSubmitting = false;
        const payload = action.payload || {};
        state.hasValidConsent = payload.isAccepted !== false;
        state.acceptedVersion =
          payload.consentVersion ?? state.acceptedVersion;
        if (payload.consentVersion) {
          state.currentVersion = payload.consentVersion;
        }
        state.expiresAt = payload.expiresAt ?? state.expiresAt;
      })
      .addCase(acceptConsent.rejected, (state, action) => {
        state.isSubmitting = false;
        state.submitError =
          action.payload || "Failed to submit consent. Please try again.";
      });
  },
});

export const { resetConsent } = consentSlice.actions;

export const selectConsentState = (state) => state.consent;

export const selectIsConsentValid = (state) =>
  computeIsConsentValid({
    hasValidConsent: state.consent.hasValidConsent,
    currentVersion: state.consent.currentVersion,
    acceptedVersion: state.consent.acceptedVersion,
    expiresAt: state.consent.expiresAt,
  });

export default consentSlice.reducer;
